/**
 * Ledger form primitives. Controls are bounded boxes with a soft radius; focus
 * is an accent border plus a soft ring. Hints say what the field is for.
 */

import React from 'react';
import { cn } from './cn';
import { TYPE } from './primitives';

/* ── Field ───────────────────────────────────────────────────────────────── */

export const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, hint, error, htmlFor, children, className }) => (
  <div className={cn('min-w-0', className)}>
    <label htmlFor={htmlFor} className={cn('text-[11.5px] font-semibold text-ink-soft block mb-1.5')}>
      {label}
    </label>
    {children}
    {hint && !error && <p className="text-[11px] text-ink-muted mt-1.5">{hint}</p>}
    {error && <p className="text-[11px] text-bad mt-1.5">{error}</p>}
  </div>
);

/* ── Controls ────────────────────────────────────────────────────────────────
   A box, because a control is a thing you put something into. The rest of the
   system draws no boxes, so this reads as an affordance rather than as trim. */

const CONTROL =
  'w-full bg-surface text-ink text-[13.5px] px-3 py-[9px] rounded-lg border border-rule-strong ' +
  'focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-accent/15 ' +
  'placeholder:text-ink-muted transition-colors duration-[120ms] disabled:opacity-40';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...rest }, ref) => <input ref={ref} {...rest} className={cn(CONTROL, className)} />
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...rest }, ref) => (
  <textarea ref={ref} {...rest} className={cn(CONTROL, 'resize-y', className)} />
));
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...rest }, ref) => (
  <select ref={ref} {...rest} className={cn(CONTROL, 'appearance-none pr-6', className)}>
    {children}
  </select>
));
Select.displayName = 'Select';

/* ── Toggle ──────────────────────────────────────────────────────────────── */

export const Toggle: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  label: React.ReactNode;
  hint?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}> = ({ checked, onChange, label, hint, disabled, className }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      'flex items-start gap-3 text-left w-full py-2 group disabled:opacity-40',
      className
    )}
  >
    <span
      className={cn(
        'mt-0.5 w-[34px] h-[18px] rounded-full border flex items-center shrink-0 transition-colors duration-[120ms]',
        checked ? 'bg-accent border-accent justify-end' : 'bg-surface border-rule-strong justify-start'
      )}
    >
      <span
        className={cn(
          'w-[14px] h-[14px] mx-[1px] rounded-full transition-colors duration-[120ms]',
          checked ? 'bg-surface' : 'bg-ink-muted'
        )}
      />
    </span>
    <span className="min-w-0">
      <span className={cn(TYPE.meta, 'block', checked ? 'text-ink' : 'text-ink-soft')}>{label}</span>
      {hint && <span className={cn(TYPE.small, 'text-ink-muted block mt-1')}>{hint}</span>}
    </span>
  </button>
);

/* ── Checkbox row ────────────────────────────────────────────────────────── */

export const CheckRow: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  label: React.ReactNode;
  className?: string;
}> = ({ checked, onChange, label, className }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={cn(
      'flex items-center gap-3 text-left py-2 px-2 -mx-2 w-full transition-colors duration-[120ms] hover:bg-ground',
      className
    )}
  >
    <span
      className={cn(
        'w-[15px] h-[15px] rounded border shrink-0',
        checked ? 'bg-accent border-accent' : 'bg-surface border-rule-strong'
      )}
    />
    <span className={cn(TYPE.meta, checked ? 'text-ink' : 'text-ink-soft')}>{label}</span>
  </button>
);
